import React, { useState } from 'react';
import './PlaylistSetupForm.css';

const SUBMIT_SESSION_URL =
  process.env.REACT_APP_SUBMIT_PLAYLIST_SESSION_URL ||
  'https://us-central1-tellynite.cloudfunctions.net/submitPlaylistSession';
const CODE_REGEX = /^[A-Z0-9]{6}$/;
const MAC_REGEX = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;

const PLAYLIST_TYPES = [
  { id: 'm3u', label: 'M3U Playlist' },
  { id: 'xtream', label: 'Xtream Codes' },
  { id: 'stalker', label: 'Stalker Portal' },
];

function normalizeServiceUrl(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  return `http://${trimmed}`;
}

function PasswordToggle({ visible, onToggle }) {
  return (
    <button
      type="button"
      className="eye-icon"
      onClick={onToggle}
      aria-label={visible ? 'Hide password' : 'Show password'}
    >
      {visible ? (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-5 0-9.27-3.11-11-7 1.21-2.73 3.29-5 6-6.32" /><path d="M1 1l22 22" /><path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5c1.38 0 2.63-.83 3.16-2.03" /></svg>
      ) : (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="12" rx="10" ry="7" /><circle cx="12" cy="12" r="3" /></svg>
      )}
    </button>
  );
}

const PlaylistSetupForm = ({ code, onSubmitted }) => {
  const [playlistType, setPlaylistType] = useState('m3u');
  const [formData, setFormData] = useState({
    m3u: { url: '' },
    xtream: { username: '', password: '', server: '', includeTv: true, includeVod: true },
    stalker: { mac: '', portal: '', username: '', password: '', deviceId: '', deviceId2: '', serial: '', includeTv: true, includeVod: true }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [checkboxError, setCheckboxError] = useState({ xtream: '', stalker: '' });
  const [fieldError, setFieldError] = useState('');
  const [showXtreamPassword, setShowXtreamPassword] = useState(false);
  const [showStalkerPassword, setShowStalkerPassword] = useState(false);

  const handleInputChange = (type, field, value) => {
    if (isSubmitting) return;
    setErrorMessage('');
    setFieldError('');
    if ((type === 'xtream' || type === 'stalker') && (field === 'includeTv' || field === 'includeVod')) {
      const otherField = field === 'includeTv' ? 'includeVod' : 'includeTv';
      const bothUnchecked = !value && !formData[type][otherField];
      if (bothUnchecked) {
        setCheckboxError(prev => ({ ...prev, [type]: 'At least one must be selected.' }));
        return;
      }
      setCheckboxError(prev => ({ ...prev, [type]: '' }));
    }
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const validateBeforeSubmit = () => {
    if (playlistType === 'm3u' && !formData.m3u.url.trim()) {
      return 'Playlist URL is required.';
    }
    if (playlistType === 'xtream') {
      if (!formData.xtream.server.trim() || !formData.xtream.username.trim() || !formData.xtream.password.trim()) {
        return 'Server URL, username, and password are required.';
      }
    }
    if (playlistType === 'stalker') {
      if (!formData.stalker.portal.trim() || !formData.stalker.mac.trim()) {
        return 'Portal URL and MAC address are required.';
      }
      if (!MAC_REGEX.test(formData.stalker.mac.trim())) {
        return 'MAC address must look like 00:1A:79:XX:XX:XX.';
      }
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const normalizedCode = (code || '').toUpperCase();
    if (!CODE_REGEX.test(normalizedCode)) {
      setErrorMessage('No setup code found. Please scan the QR code again or enter the code manually.');
      return;
    }

    const validationError = validateBeforeSubmit();
    if (validationError) {
      setFieldError(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setFieldError('');

    try {
      let payload = {};
      if (playlistType === 'm3u') {
        payload = { url: normalizeServiceUrl(formData.m3u.url) };
      } else if (playlistType === 'xtream') {
        payload = {
          server: normalizeServiceUrl(formData.xtream.server),
          username: formData.xtream.username.trim(),
          password: formData.xtream.password,
          includeTv: formData.xtream.includeTv,
          includeVod: formData.xtream.includeVod
        };
      } else if (playlistType === 'stalker') {
        payload = {
          portal: normalizeServiceUrl(formData.stalker.portal),
          mac: formData.stalker.mac.trim().toUpperCase(),
          username: formData.stalker.username,
          password: formData.stalker.password,
          deviceId: formData.stalker.deviceId,
          deviceId2: formData.stalker.deviceId2,
          serial: formData.stalker.serial,
          includeTv: formData.stalker.includeTv,
          includeVod: formData.stalker.includeVod
        };
      }

      const response = await fetch(SUBMIT_SESSION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: normalizedCode,
          type: playlistType,
          payload
        })
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData.message || 'Submission failed');
      }

      if (typeof onSubmitted === 'function') {
        onSubmitted(normalizedCode);
      }
    } catch (error) {
      console.error('Error submitting data:', error);
      setErrorMessage(error.message || 'Error submitting data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formLocked = isSubmitting;

  const renderM3UForm = () => (
    <div className="form-section">
      <h3>M3U Playlist</h3>
      <div className="form-group">
        <label htmlFor="m3u-url">Playlist URL *</label>
        <input
          type="text"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          id="m3u-url"
          value={formData.m3u.url}
          onChange={(e) => handleInputChange('m3u', 'url', e.target.value)}
          placeholder="https://example.com/playlist.m3u"
          required
          disabled={formLocked}
        />
      </div>
    </div>
  );

  const renderXtreamForm = () => (
    <div className="form-section">
      <h3>Xtream Codes</h3>
      <div className="form-group">
        <label htmlFor="xtream-server">Server URL *</label>
        <input
          type="text"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          id="xtream-server"
          value={formData.xtream.server}
          onChange={(e) => handleInputChange('xtream', 'server', e.target.value)}
          placeholder="http://example.com:port"
          required
          disabled={formLocked}
        />
      </div>
      <div className="form-group">
        <label htmlFor="xtream-username">Username *</label>
        <input
          type="text"
          id="xtream-username"
          value={formData.xtream.username}
          onChange={(e) => handleInputChange('xtream', 'username', e.target.value)}
          placeholder="Enter username"
          required
          disabled={formLocked}
          autoCapitalize="off"
          autoCorrect="off"
        />
      </div>
      <div className="form-group">
        <label htmlFor="xtream-password">Password *</label>
        <div className="input-eye-wrapper">
          <input
            type={showXtreamPassword ? 'text' : 'password'}
            id="xtream-password"
            value={formData.xtream.password}
            onChange={(e) => handleInputChange('xtream', 'password', e.target.value)}
            placeholder="Enter your password"
            required
            disabled={formLocked}
          />
          <PasswordToggle
            visible={showXtreamPassword}
            onToggle={() => setShowXtreamPassword(v => !v)}
          />
        </div>
      </div>
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={formData.xtream.includeTv}
            onChange={e => handleInputChange('xtream', 'includeTv', e.target.checked)}
            disabled={formLocked}
          />
          Include TV channels
        </label>
      </div>
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={formData.xtream.includeVod}
            onChange={e => handleInputChange('xtream', 'includeVod', e.target.checked)}
            disabled={formLocked}
          />
          Include VOD
        </label>
      </div>
      {checkboxError.xtream && <div className="checkbox-error">{checkboxError.xtream}</div>}
    </div>
  );

  const renderStalkerForm = () => (
    <div className="form-section">
      <h3>Stalker Portal</h3>
      <div className="form-group">
        <label htmlFor="stalker-portal">Portal URL *</label>
        <input
          type="text"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          id="stalker-portal"
          value={formData.stalker.portal}
          onChange={(e) => handleInputChange('stalker', 'portal', e.target.value)}
          placeholder="http://example.com/portal"
          required
          disabled={formLocked}
        />
      </div>
      <div className="form-group">
        <label htmlFor="stalker-mac">MAC Address *</label>
        <input
          type="text"
          id="stalker-mac"
          value={formData.stalker.mac}
          onChange={(e) => handleInputChange('stalker', 'mac', e.target.value)}
          placeholder="00:1A:79:XX:XX:XX"
          required
          disabled={formLocked}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />
        <small>Format: 00:1A:79:XX:XX:XX (copy from your TV app settings)</small>
      </div>
      <div className="form-group">
        <label htmlFor="stalker-username">Username (optional)</label>
        <input
          type="text"
          id="stalker-username"
          value={formData.stalker.username}
          onChange={(e) => handleInputChange('stalker', 'username', e.target.value)}
          placeholder="Enter username (optional)"
          disabled={formLocked}
          autoCapitalize="off"
          autoCorrect="off"
        />
      </div>
      <div className="form-group">
        <label htmlFor="stalker-password">Password (optional)</label>
        <div className="input-eye-wrapper">
          <input
            type={showStalkerPassword ? 'text' : 'password'}
            id="stalker-password"
            value={formData.stalker.password}
            onChange={(e) => handleInputChange('stalker', 'password', e.target.value)}
            placeholder="Enter your password"
            disabled={formLocked}
          />
          <PasswordToggle
            visible={showStalkerPassword}
            onToggle={() => setShowStalkerPassword(v => !v)}
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="stalker-deviceid">Device ID (optional)</label>
        <input
          type="text"
          id="stalker-deviceid"
          value={formData.stalker.deviceId}
          onChange={(e) => handleInputChange('stalker', 'deviceId', e.target.value)}
          placeholder="Device ID (optional)"
          disabled={formLocked}
        />
      </div>
      <div className="form-group">
        <label htmlFor="stalker-deviceid2">Device ID 2 (optional)</label>
        <input
          type="text"
          id="stalker-deviceid2"
          value={formData.stalker.deviceId2}
          onChange={(e) => handleInputChange('stalker', 'deviceId2', e.target.value)}
          placeholder="Device ID 2 (optional)"
          disabled={formLocked}
        />
      </div>
      <div className="form-group">
        <label htmlFor="stalker-serial">Serial number (optional)</label>
        <input
          type="text"
          id="stalker-serial"
          value={formData.stalker.serial}
          onChange={(e) => handleInputChange('stalker', 'serial', e.target.value)}
          placeholder="Serial number (optional)"
          disabled={formLocked}
        />
      </div>
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={formData.stalker.includeTv}
            onChange={e => handleInputChange('stalker', 'includeTv', e.target.checked)}
            disabled={formLocked}
          />
          Include TV channels
        </label>
      </div>
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={formData.stalker.includeVod}
            onChange={e => handleInputChange('stalker', 'includeVod', e.target.checked)}
            disabled={formLocked}
          />
          Include VOD
        </label>
      </div>
      {checkboxError.stalker && <div className="checkbox-error">{checkboxError.stalker}</div>}
    </div>
  );

  return (
    <div className="playlist-setup-container">
      <div className="header">
        <h1>TellyNite Playlist Setup</h1>
        {code && (
          <div className="setup-code">
            <p>Setup Code: <strong>{code}</strong></p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="playlist-form">
        <div className="playlist-type-selector">
          <h2>Select Playlist Type</h2>
          <div className="type-buttons" role="group" aria-label="Playlist type">
            {PLAYLIST_TYPES.map(({ id, label }) => {
              const selected = playlistType === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`type-btn${selected ? ' active' : ''}`}
                  onClick={() => !formLocked && setPlaylistType(id)}
                  aria-pressed={selected}
                  disabled={formLocked}
                >
                  {selected && <span className="type-btn-check" aria-hidden="true">✓</span>}
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {playlistType === 'm3u' && renderM3UForm()}
        {playlistType === 'xtream' && renderXtreamForm()}
        {playlistType === 'stalker' && renderStalkerForm()}

        {fieldError && (
          <div className="status-message error" role="alert">
            {fieldError}
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            disabled={formLocked}
            className="submit-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Playlist'}
          </button>
        </div>

        {errorMessage && (
          <div className="status-message error" role="alert">
            {errorMessage}
          </div>
        )}
      </form>
    </div>
  );
};

export default PlaylistSetupForm;
